import classNames from 'classnames';
import { CSSProperties, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import {
  mobileAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import player1 from './assets/player1.png';
import player2 from './assets/player2.png';
import player3 from './assets/player3.png';
import player4 from './assets/player4.png';
import player5 from './assets/player5.png';
import player6 from './assets/player6.png';
import player7 from './assets/player7.png';
import player8 from './assets/player8.png';
import player9 from './assets/player9.png';

const delayAfterFirstStep = 500;
const delayBeforeFirstHalo = 400;
const delayBeforeSecondHalo = 600;
const haloAnimationDuration = 500;
const dimAnimationDuration = 300;

const appear = keyframes`
    from {
        opacity: 0;
        transform: scale(1.2);
    }
    20% {
      opacity: 1;
      transform: scale(1.18);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
`;
const brightnessAnimation = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;
const glowAppear = keyframes`
    0% {
        opacity: 0;
        transform: scale(0.5);
    }
    100% {
        opacity: 0.5;
        transform: scale(1.2);
    }
`;
const haloAnimation = keyframes`
    0% {
        opacity: 0;
        transform: scale(0.5);
    }
    50% {
        opacity: 1;
        transform: scale(1.2);
    }
    100% {
        opacity: 0;
        transform: scale(1.2);
    }
`;
const highlight = keyframes`
    0%{
        transform: scale(1);
    }
    20% {

        transform: scale(0.85);
    }
    100% {
        transform: scale(1.3);
    }
`;

const Root = styled.div`
  width: calc(28 * var(--unit));
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--unit);
  position: relative;

  @media ${mobileAndAbove} {
    width: calc(40 * var(--unit));
  }

  @media ${tabletAndAbove} {
    width: calc(60 * var(--unit));
  }
`;
const ImgWrapper = styled.div`
  position: relative;
  opacity: 0;
  transform: scale(1.2);
  width: 100%;
  aspect-ratio: var(--card-aspect-ratio);
  will-change: transform, opacity;
  animation: ${appear} 200ms forwards var(--animation-delay);

  &.highlight {
    opacity: 1;
    transform: scale(1);
    animation: ${highlight} 1000ms forwards ${delayAfterFirstStep}ms;
    z-index: 2;
  }
`;
const HighlightGlow = styled.div`
  position: absolute;
  inset: 0;
  border-radius: var(--double-unit);
  z-index: -2;
  background-color: var(--c-yellow-500);
  filter: blur(var(--double-unit));
  transform: scale(0.5);
  opacity: 0;
  animation: ${glowAppear} 300ms forwards ${delayAfterFirstStep}ms;
`;
const Halo = styled.div`
  position: absolute;
  inset: 0;
  border-radius: var(--unit);
  z-index: -1;
  border: 1px solid var(--c-yellow-400);
  transform: scale(0.5);
  opacity: 0;
  animation: ${haloAnimation} ${haloAnimationDuration}ms forwards
    ${delayAfterFirstStep + delayBeforeFirstHalo}ms;
`;
const BlurredHalo = styled(Halo)`
  filter: blur(2px);
`;
const CrispHalo = styled(Halo)`
  border-width: 2px;
  filter: drop-shadow(0 0 4px var(--c-yellow-500));
`;
const BlurredHalo2 = styled(BlurredHalo)`
  animation: ${haloAnimation} ${haloAnimationDuration}ms forwards
    ${delayAfterFirstStep + delayBeforeSecondHalo}ms;
`;
const CrispHalo2 = styled(CrispHalo)`
  animation: ${haloAnimation} ${haloAnimationDuration}ms forwards
    ${delayAfterFirstStep + delayBeforeSecondHalo}ms;
`;
const PlayerImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;
const PlayerGlowImg = styled(PlayerImg)`
  position: absolute;
  inset: 0;
  filter: brightness(1.5) drop-shadow(0 0 2px var(--c-yellow-500));
  animation: ${brightnessAnimation} 400ms forwards var(--animation-delay);
`;
const DimmedLayer = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 0;
  border-radius: 4%;
  transition: opacity ${dimAnimationDuration}ms ${delayAfterFirstStep}ms;
  &.appear {
    opacity: 1;
  }
`;
const players = [
  player1,
  player2,
  player3,
  player4,
  player5,
  player6,
  player7,
  player8,
  player9,
];

const centralImageIndex = 4;

export const RecruitPlayersAnimation = () => {
  const [firstStepDone, setFirstStepDone] = useState(false);

  return (
    <Root>
      {players.map((player, index) => {
        const highlightCard = index === centralImageIndex && firstStepDone;
        const dimCard = index !== centralImageIndex && firstStepDone;

        return (
          <ImgWrapper
            key={player}
            className={classNames({
              highlight: highlightCard,
            })}
            style={
              {
                '--animation-delay': `${index * 70}ms`,
              } as CSSProperties
            }
            onAnimationEnd={
              index === players.length - 1
                ? () => {
                    setFirstStepDone(true);
                  }
                : undefined
            }
          >
            {!!highlightCard && (
              <>
                <HighlightGlow />
                <BlurredHalo />
                <CrispHalo />
                <BlurredHalo2 />
                <CrispHalo2 />
              </>
            )}
            <PlayerImg src={player} alt="" />
            <PlayerGlowImg src={player} alt="" />
            <DimmedLayer className={classNames({ appear: dimCard })} />
          </ImgWrapper>
        );
      })}
    </Root>
  );
};
